<template>
    <filters-wrapper :has-active-filters="hasActiveFilters" @submit="submit" @reset="reset">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="submit" />
        </template>
        <v-form class="mb-5 table-filters" data-cy="communications-filters-form">
            <v-row dense>
                <v-col cols="12" md="6">
                    <daterange
                        :start-date-label="$t('communication_date_from')"
                        :end-date-label="$t('communication_date_to')"
                        dense
                        :outlined="false"
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.communicationDateFrom"
                        :_end-date="filters.communicationDateTo"
                        clearable
                        data-cy="date-range-communication-date"
                        @change-start-date="filters.communicationDateFrom = $event"
                        @change-end-date="filters.communicationDateTo = $event"
                    />
                </v-col>
                <v-col v-if="!companyPageMode" cols="12" md="3">
                    <v-select
                        v-model="filters.accountType"
                        :items="accountTypeItems"
                        :label="$t('account_type')"
                        clearable
                        density="compact"
                        color="primary"
                        data-cy="account-type"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-text-field
                        v-model="filters.subject"
                        :label="$t('subject')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="subject"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <daterange
                        :start-date-label="$t('created_date_from')"
                        :end-date-label="$t('created_date_to')"
                        dense
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.createdDateFrom"
                        :_end-date="filters.createdDateTo"
                        clearable
                        data-cy="date-range-created"
                        @change-start-date="filters.createdDateFrom = $event"
                        @change-end-date="filters.createdDateTo = $event"
                    />
                </v-col>
                <v-col v-if="!companyPageMode" cols="12" md="3">
                    <v-text-field
                        v-model="filters.accountName"
                        :label="$t('account_name')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="account-name"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-select
                        v-model="filters.stage"
                        :items="stageItems"
                        :label="$t('stage')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="stage"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <daterange
                        :start-date-label="$t('modified_date_from')"
                        :end-date-label="$t('modified_date_to')"
                        dense
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.modifiedDateFrom"
                        :_end-date="filters.modifiedDateTo"
                        clearable
                        data-cy="date-range-modified"
                        @change-start-date="filters.modifiedDateFrom = $event"
                        @change-end-date="filters.modifiedDateTo = $event"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-select
                        v-model="filters.type"
                        :items="typeItems"
                        :label="$t('communication_type')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="communication-type"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-autocomplete
                        v-model="filters.assignedToId"
                        item-title="name"
                        item-value="personId"
                        :items="users"
                        :label="$t('assigned_to')"
                        :loading="usersLoading"
                        :disabled="usersLoading"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="assigned-to-id"
                    />
                    <v-checkbox
                        v-model="isChecked"
                        :label="$t('assigned_to_me')"
                        density="compact"
                        hide-details
                        class="float-right"
                        color="primary"
                        data-cy="assigned-to-me"
                        @update:model-value="$event ? (filters.assignedToId = personId) : (filters.assignedToId = null)"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import Daterange from '@/components/search/forms/Daterange.vue'

    @Component({
        components: {Daterange},
    })
    export class CommunicationsFilters extends Vue {
        @VModel() filters
        @Prop({required: true, type: Boolean}) hasActiveFilters: boolean
        @Prop({default: false, type: Boolean}) companyPageMode: boolean
        @Watch('filters.assignedToId')
        onLocaleChange() {
            this.isChecked = this.filters.assignedToId === this.personId
        }

        usersLoading: boolean = true
        users = []
        isChecked: boolean = false

        @Emit()
        submit() {}

        @Emit()
        reset() {}

        async mounted() {
            try {
                this.usersLoading = true
                this.users = (await this.$api.persons.get({companyId: authStore.company.id})).users
                    .map(({personId, firstName, lastName}) => ({
                        personId,
                        name: `${lastName || ''} ${firstName || ''}`.trim(),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            } catch (e) {
                this.users = []
            } finally {
                this.usersLoading = false
            }
        }

        get accountTypeItems() {
            const cc = this.$conf.public.account.showCorporateClients ? ['CORPORATE_CLIENT'] : []
            const ta = this.$conf.public.account.showAgencies ? ['AGENCY_DISTRIBUTOR'] : []
            const accountTypes = [...cc, 'PRIVATE_CLIENT', 'SUPPLIER', ...ta, 'SYSTEM']
            return accountTypes.map(value => ({value, title: this.$t(`accountTypes.${value}`)}))
        }

        get stageItems() {
            const accountTypes = ['HELD', 'NOT_HELD', 'PLANNED']
            return accountTypes.map(value => ({value, title: this.$t(`communicationStage.${value}`)}))
        }

        get typeItems() {
            const accountTypes = ['CALL', 'MEETING', 'EMAIL', 'OTHER']
            return accountTypes.map(value => ({value, title: this.$t(`communicationType.${value}`)}))
        }

        get personId() {
            return authStore.person.personId
        }
    }

    export default CommunicationsFilters
</script>
